import { useState, useEffect, useRef } from "react";
import CartTotals from "../components/cart/CartTotals";
import Categories from "../components/categories/Categories";
import Header from "../components/header/Header";
import Products from "../components/products/Products";
import { Skeleton } from "antd";
import { apiRequest } from "../utils/apiClient";
import { useDispatch, useSelector } from "react-redux";
import { reset } from "../redux/cartSlice";
import { setSelectedBranch } from "../redux/userSlice";
import { useTranslation } from "react-i18next";

function HomePage() {
  const { t } = useTranslation("pages");
  const [categories, setCategories] = useState([]);
  const [searched, setSearched] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const searchInputRef = useRef(null);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const isAdmin = user?.role !== "cashier";
  const selectedBranchId = isAdmin ? user?.selectedBranchId : null;

  const fetchCategories = async () => {
    try {
      const url = selectedBranchId
        ? `/api/categories/get-all?branchId=${encodeURIComponent(selectedBranchId)}`
        : "/api/categories/get-all";
      const data = await apiRequest(url);
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    setCategoriesLoading(true);
    fetchCategories();
  }, [selectedBranchId]);

  // Keep selected category consistent across add/update/delete actions
  useEffect(() => {
    const handleCategoryChanged = (event) => {
      const detail = event?.detail || {};
      if (detail.type === "updated" && detail.oldTitle && detail.newTitle) {
        setSelectedCategory((prev) => (prev === detail.oldTitle ? detail.newTitle : prev));
      }
      if (detail.type === "deleted") {
        setSelectedCategory("All");
      }
    };

    window.addEventListener("categoryChanged", handleCategoryChanged);
    return () => {
      window.removeEventListener("categoryChanged", handleCategoryChanged);
    };
  }, []);

  // Keyboard shortcut: press "2" anywhere on this page to focus the barcode scan input
  useEffect(() => {
    const handleFocusBarcodeKeyDown = (event) => {
      if (event.key !== "2") return;
      const activeTag = document.activeElement?.tagName;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(activeTag || "")) return;
      event.preventDefault();
      window.dispatchEvent(new CustomEvent("focusBarcodeInput"));
    };
    window.addEventListener("keydown", handleFocusBarcodeKeyDown);
    return () => window.removeEventListener("keydown", handleFocusBarcodeKeyDown);
  }, []);

  // Keyboard shortcut: press "1" anywhere on this page to focus the search bar
  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      if (event.key !== "1") return;

      const activeTag = document.activeElement?.tagName;
      // Don't steal focus if the user is already typing in an input-like element
      if (["INPUT", "TEXTAREA", "SELECT"].includes(activeTag || "")) {
        return;
      }

      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, []);

  // Keyboard shortcut: press "0" anywhere on this page to generate invoice
  useEffect(() => {
    const handleCreateInvoiceKeyDown = (event) => {
      if (event.key !== "0") return;

      const activeTag = document.activeElement?.tagName;
      // Don't trigger if the user is already typing in an input-like element
      if (["INPUT", "TEXTAREA", "SELECT"].includes(activeTag || "")) {
        return;
      }

      event.preventDefault();
      // Dispatch custom event to trigger invoice creation
      window.dispatchEvent(new CustomEvent("createInvoiceShortcut"));
    };

    window.addEventListener("keydown", handleCreateInvoiceKeyDown);
    return () => {
      window.removeEventListener("keydown", handleCreateInvoiceKeyDown);
    };
  }, []);

  // Keyboard shortcut: press "9" to go to basket then open create-invoice (for entering customer/discount)
  useEffect(() => {
    const handleGoToCartAndCreateInvoice = (event) => {
      if (event.key !== "9") return;

      const activeTag = document.activeElement?.tagName;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(activeTag || "")) {
        return;
      }

      event.preventDefault();
      window.dispatchEvent(new CustomEvent("goToCartAndCreateInvoice"));
    };

    window.addEventListener("keydown", handleGoToCartAndCreateInvoice);
    return () => {
      window.removeEventListener("keydown", handleGoToCartAndCreateInvoice);
    };
  }, []);

  // Listen for global "3" key shortcut to focus search bar
  useEffect(() => {
    const handleFocusSearch = () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    };

    window.addEventListener("focusHomepageSearch", handleFocusSearch);
    return () => {
      window.removeEventListener("focusHomepageSearch", handleFocusSearch);
    };
  }, []);

  // Keyboard shortcut: press "5" anywhere on this page to clear the cart
  useEffect(() => {
    const handleClearCartKeyDown = (event) => {
      if (event.key !== "5") return;

      const activeTag = document.activeElement?.tagName;
      // Don't trigger if the user is already typing in an input-like element
      if (["INPUT", "TEXTAREA", "SELECT"].includes(activeTag || "")) {
        return;
      }

      event.preventDefault();
      // Dispatch reset action to clear the cart
      dispatch(reset());
    };

    window.addEventListener("keydown", handleClearCartKeyDown);
    return () => {
      window.removeEventListener("keydown", handleClearCartKeyDown);
    };
  }, [dispatch]);

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Static */}
      <Header setSearched={setSearched} searchInputRef={searchInputRef} />

      <div className="home px-6 flex md:flex-row flex-col justify-between gap-10 flex-1 min-h-0 overflow-hidden">
        {/* Categories Section */}
        <div className="categories overflow-y-auto overflow-x-visible min-h-[125px] pb-10 md:h-full">
          {categoriesLoading ? (
            <div className="flex justify-center items-center h-40">
              <div>
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="min-h-[180px] border rounded-md p-4 w-[180px] mb-2"
                  >
                    <Skeleton
                      active
                      avatar={{ shape: "square", size: 64 }}
                      title={{ width: "100%" }}
                      paragraph={{ rows: 1 }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Categories
              categories={categories}
              setCategories={setCategories}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
          )}
        </div>

        {/* Products Section */}
        <div className="products flex-[8] overflow-y-auto pb-10 min-h-[500px] md:h-full">
          <Products
            categories={categories}
            selectedCategory={selectedCategory}
            searched={searched}
          />
        </div>

        {/* Static */}
        <div className="cart-totals md:pb-0 pb-12 md:h-full md:flex md:flex-col">
          <CartTotals />
        </div>
      </div>
    </div>
  );
}

export default HomePage;
